// Motion only on state change: numbers count to a new value, success
// draws itself once. Everything here collapses to the final state under prefers-reduced-motion.
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { localizeDecimal } from "../lib/money";

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;

/**
 * A plain decimal string ("21.50") that counts from `from` (or its previous value) to `value` when it
 * changes. The final frame is always the exact string, never a float rendering.
 */
export function useCountUp(value: string, opts: { from?: string; durationMs?: number; animate?: boolean } = {}): string {
  const { from, durationMs = 900, animate = true } = opts;
  const decimals = value.split(".")[1]?.length ?? 0;
  const [shown, setShown] = useState(() => (animate && from !== undefined && !prefersReducedMotion() ? from : value));
  const prev = useRef(from ?? value);

  useEffect(() => {
    const start = Number(prev.current);
    const end = Number(value);
    prev.current = value;
    if (!animate || prefersReducedMotion() || !Number.isFinite(start) || !Number.isFinite(end) || start === end) {
      setShown(value);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const frame = (now: number) => {
      const t = Math.min(1, (now - t0) / durationMs);
      if (t >= 1) {
        setShown(value);
        return;
      }
      setShown((start + (end - start) * easeOut(t)).toFixed(decimals));
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [value, animate, durationMs, decimals]);

  return shown;
}

/** The one hero number of a view: big, tabular, the unit muted beside it. */
export function HeroAmount({
  value,
  symbol,
  from,
  animate = true,
  size = "lg",
  label,
}: {
  /** Plain decimal string. */
  value: string;
  symbol: string;
  from?: string;
  animate?: boolean;
  size?: "lg" | "md";
  /** Accessible text; the animated figure itself is hidden from screen readers while it moves. */
  label?: string;
}) {
  const { locale } = useI18n();
  const shown = useCountUp(value, { from, animate });
  const { whole, fraction, decimal } = localizeDecimal(shown, locale);
  const exact = localizeDecimal(value, locale);
  return (
    <span className={`hero-amount hero-${size}`}>
      <span className="sr-only">
        {label ? `${label}: ` : ""}
        {exact.whole}
        {exact.fraction ? `${exact.decimal}${exact.fraction}` : ""} {symbol}
      </span>
      <span className="hero-figure num" aria-hidden="true">
        <span className="hero-whole">{whole}</span>
        {fraction ? (
          <span className="hero-fraction">
            {decimal}
            {fraction}
          </span>
        ) : null}
      </span>
      <span className="hero-unit" aria-hidden="true">
        {symbol}
      </span>
    </span>
  );
}

/** Success mark: a ring that blooms once and a check that draws itself. */
export function SuccessMark({ tone = "success" }: { tone?: "success" | "warning" | "neutral" }) {
  return (
    <span className={`success-mark success-mark-${tone}`} aria-hidden="true">
      <span className="success-ring" />
      <span className="success-ring success-ring-2" />
      <svg viewBox="0 0 52 52" width="52" height="52">
        <circle cx="26" cy="26" r="24" className="success-disc" />
        <path d="M15.5 27.2l7 7L37 19.5" className="success-check" />
      </svg>
    </span>
  );
}
